import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Send, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const STATUS_CLS = { pending: "text-amber-300", approved: "text-emerald-300", rejected: "text-red-300", cancelled: "text-slate-500" };

export function UsernameRequest() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [form, setForm] = useState({ username: "", reason: "" });
  const [busy, setBusy] = useState(false);
  const { data } = useQuery({ queryKey: ["username-requests"], queryFn: () => api.get("/me/username-requests").then((r) => r.data.items) });
  const pending = data?.find((r) => r.status === "pending");
  const last = data?.find((r) => r.status !== "pending");
  const refresh = () => qc.invalidateQueries({ queryKey: ["username-requests"] });
  const submit = async () => {
    setBusy(true);
    try { await api.post("/me/username-requests", form); toast.success("Request sent to the administrators"); setForm({ username: "", reason: "" }); refresh(); }
    catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };
  const cancel = async () => {
    try { await api.delete(`/me/username-requests/${pending.request_id}`); toast.success("Request cancelled"); refresh(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <div className="space-y-2" data-testid="username-request">
      <p className="field-label">Username</p>
      <p className="font-mono text-sm text-slate-200" data-testid="settings-current-username">@{user.username}</p>
      {pending ? (
        <div className="flex flex-wrap items-center gap-2 rounded border border-amber-800/50 bg-amber-950/20 p-2 text-xs text-amber-100" data-testid="username-request-pending">
          Waiting for approval: <span className="font-mono">@{pending.requested_username}</span>
          <button type="button" className="btn-ghost ml-auto px-2 py-1 text-xs" onClick={cancel} data-testid="username-request-cancel"><X className="h-3 w-3" />Cancel</button>
        </div>
      ) : (
        <div className="space-y-2 rounded border border-[#2A3447] p-2">
          <p className="text-xs text-slate-500">Username changes are approved by an administrator. 3-30 characters: letters, numbers, _ or -.</p>
          <input className="field text-sm" placeholder="New username" value={form.username} maxLength={30} onChange={(e) => setForm({ ...form, username: e.target.value.toLowerCase() })} aria-label="New username" data-testid="username-request-input" />
          <input className="field text-sm" placeholder="Reason (optional)" value={form.reason} maxLength={300} onChange={(e) => setForm({ ...form, reason: e.target.value })} aria-label="Reason" data-testid="username-request-reason" />
          <button type="button" className="btn-outline text-xs" disabled={busy || form.username.length < 3} onClick={submit} data-testid="username-request-submit">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}Request change
          </button>
        </div>
      )}
      {last && (
        <p className="text-xs text-slate-500" data-testid="username-request-last">
          Last request @{last.requested_username}: <span className={STATUS_CLS[last.status]}>{last.status}</span>{last.note && ` · ${last.note}`}
        </p>
      )}
    </div>
  );
}
